<template>
  <div class="ib-login-root">
    <!-- Language selector top-right -->
    <div class="language-selector-container white-text">
      <LanguageSelector isLoginFlow />
    </div>

    <v-container class="login-container" fluid>
      <v-row class="login-row">
        <!-- Left pane: form -->
        <v-col class="pane-left d-flex align-center justify-center" cols="5">
          <div class="form-wrap">
            <!-- Partner logo or Swiset fallback -->
            <v-img
              v-if="partnerLogo"
              :src="partnerLogo"
              max-width="250"
              max-height="130"
              contain
              style="height: 130px"
            />
            <v-img
              v-else
              src="@/assets/Logo_Main.svg"
              max-width="250"
              contain
              style="height: 130px"
            />

            <v-form v-model="valid" class="login-form">
              <br />
              <!-- Subtle IB portal indicator -->
              <div class="ib-portal-badge">
                <v-icon size="14" :color="primaryColor">fa-regular fa-users</v-icon>
                <span>IB Portal</span>
              </div>

              <v-text-field
                :label="$t('form_Login.username.label')"
                v-model="username"
                v-on:keyup.enter="submitForm"
                outlined
              />
              <v-text-field
                :label="$t('form_Login.password.label')"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                v-on:keyup.enter="submitForm"
                outlined
              >
                <v-icon
                  style="font-size: 16px;"
                  slot="append"
                  class="icon"
                  @click="showPassword = !showPassword"
                >
                  {{ showPassword ? "fa-eye" : "fa-eye-slash" }}
                </v-icon>
              </v-text-field>

              <div class="d-flex align-center justify-end text-end mb-5">
                <a
                  href="#"
                  class="forgot-link"
                  @click.prevent="forgotPassword"
                >{{ $t("lostPassword") }}</a>
              </div>

              <br />

              <v-row class="justify-space-between">
                <v-btn
                  :loading="loading"
                  :style="loginBtnStyle"
                  class="login-btn"
                  @click="submitForm"
                >
                  {{ $t("login") }}
                </v-btn>
              </v-row>

              <!-- Powered by footer under form -->
              <div class="powered-by">
                {{ $t('ib.login.poweredBy') }}
              </div>
            </v-form>
          </div>
        </v-col>

        <!-- Right pane: branded image -->
        <v-col
          class="login-image"
          cols="7"
        >
        </v-col>
      </v-row>
    </v-container>

    <!-- Error dialog (reuses trader's LoginError component) -->
    <v-overlay :dark="false" v-model="errorDialog">
      <LoginError
        :type="errorType"
        :email="username"
        :password="password"
        @close="errorDialog = false"
      />
    </v-overlay>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import AuthRepository from '@/repositories/auth'
import LoginError from '@/views/Login/components/LoginError'
import LanguageSelector from '@/components/LanguageSelector'

export default {
  name: 'IBLogin',
  components: { LoginError, LanguageSelector },

  data() {
    return {
      valid: false,
      username: '',
      password: '',
      showPassword: false,
      loading: false,
      errorDialog: false,
      errorType: ''
    }
  },

  computed: {
    partnerLogo() {
      return this.$store.getters['ib/partnerLogoUrl']
    },

    primaryColor() {
      return getComputedStyle(document.documentElement)
        .getPropertyValue('--ib-primary').trim() || '#6366F1'
    },

    loginBtnStyle() {
      return {
        backgroundColor: `${this.primaryColor} !important`,
        color: '#fff !important',
        borderRadius: '3px',
        padding: '5% 7.7%'
      }
    },

  },

  methods: {
    ...mapMutations([
      'setToken',
      'setUser',
      'setUserDetails',
      'setPreferredLanguage'
    ]),

    async submitForm() {
      if (!this.username || !this.password) return

      this.loading = true

      try {
        const response = await AuthRepository.login(this.username, this.password)

        if (response.status < 250) {
          this.setToken(`Bearer ${response.data.id_token}`)

          const userResponse = await AuthRepository.getUser()
          this.setUser(userResponse.data)

          const langResponse = await AuthRepository.getLanguage()
          this.setPreferredLanguage(langResponse.data)

          const detailsResponse = await AuthRepository.getUserDetails()
          this.setUserDetails(detailsResponse.data)

          // IB always redirects to communities after login
          const redirect = this.$route.query.redirect
          if (redirect && redirect !== '/login') {
            this.$router.push(redirect)
          } else {
            this.$router.push('/communities')
          }
        }
      } catch (error) {
        this.loading = false
        this.errorDialog = true

        if (
          error.response?.data?.status === 401 &&
          String(error.response.data.detail).endsWith('not activated')
        ) {
          this.errorType = 'NOT_ACTIVATED'
        } else if (error.response?.data?.status === 401) {
          this.errorType = 'NOT_CREDENTIALS'
        } else {
          this.errorType = 'UNEXPECTED'
        }
      }
    },

    forgotPassword() {
      window.open(`${window.location.origin}/reset-password`, '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
.language-selector-container {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 100;
}

.white-text :deep(.language-selector-trigger) {
  color: white !important;
}

.white-text :deep(.language-name),
.white-text :deep(.dropdown-icon) {
  color: white !important;
}

.v-input__icon--append .v-icon {
  border: none !important;
}

.v-input__icon--append .v-icon:hover {
  border: none !important;
}

.ib-login-root {
  height: 100vh;
  overflow: hidden;
  padding: 0 !important;
}

.login-container {
  height: 100vh !important;
  max-height: 100vh !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden;
}

.login-row {
  height: 100vh !important;
  max-height: 100vh !important;
}

.pane-left {
  height: 100vh;
  padding: 40px 4%;
}

.form-wrap {
  width: 100%;
  max-width: 400px;
}

.ib-portal-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  background: rgba(99, 102, 241, 0.08);
  background: color-mix(in srgb, var(--ib-primary, #6366F1) 8%, transparent);
  margin-bottom: 20px;

  span {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: var(--ib-primary, #6366F1);
    text-transform: uppercase;
  }
}

.forgot-link {
  color: var(--v-mediumGray-base) !important;
  font-size: 14px;
  text-decoration: none;

  &:hover {
    color: var(--ib-primary, #6366F1) !important;
  }
}

.login-btn {
  min-width: 120px !important;
}

.login-image {
  padding: 0;
  height: 100vh;
  background-color: #111111;
  background-image: url("https://hub.swiset.com/wp-content/uploads/2024/02/Swiset-Best-AI-Trading-Journal.jpg");
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
}

.powered-by {
  margin-top: 32px;
  font-size: 12px;
  color: #bbb;
  text-align: center;
}

@media (max-width: 960px) {
  .pane-left {
    padding: 24px 4%;
  }
}

.login-form {
  width: 100%;
}
</style>
